// pages/users/UserHierarchyPage.jsx
import { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Users, RefreshCw } from 'lucide-react';
import { userController } from '../../controllers/userController.js';
import { ROLE_COLORS, ROLE_LABELS } from '../../models/User.js';
import { C } from '../../hooks/useUsers.js';

function TreeNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const children    = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  const color       = ROLE_COLORS[node.role] || '#64748b';
  const initials    = node.initials ||
    `${node.firstName?.[0] || ''}${node.lastName?.[0] || ''}`.toUpperCase() || '?';
  const fullName    = node.fullName || `${node.firstName || ''} ${node.lastName || ''}`.trim();

  return (
    <div style={{ marginLeft: depth > 0 ? '24px' : '0' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
          backgroundColor: open && hasChildren ? `${color}08` : 'transparent',
          borderLeft: depth > 0 ? `2px solid ${color}30` : 'none',
          marginBottom: '4px', transition: 'background .15s',
        }}
        onClick={() => setOpen(o => !o)}
      >
        <span style={{ color, flexShrink: 0, width: '16px' }}>
          {hasChildren
            ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
            : <span style={{ display:'inline-block', width:'14px', height:'14px', borderRadius:'50%', backgroundColor:`${color}30`, verticalAlign:'middle' }} />
          }
        </span>

        <div style={{
          width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
          backgroundColor: `${color}20`, border: `2px solid ${color}50`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: '700', color,
        }}>
          {initials}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {fullName}
            <span style={{ ...C.badge, backgroundColor: `${color}15`, color, fontSize: '10px' }}>{ROLE_LABELS[node.role] || node.role}</span>
            {!node.isActive && <span style={{ ...C.badge, backgroundColor: '#fee2e2', color: '#dc2626', fontSize: '10px' }}>Inactive</span>}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{node.customId} · {node.email}</div>
        </div>

        {hasChildren && (
          <span style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>
            {children.length} {children.length === 1 ? 'member' : 'members'}
          </span>
        )}
      </div>

      {open && hasChildren && (
        <div style={{ borderLeft: `1px dashed ${color}25`, marginLeft: '19px', paddingLeft: '6px' }}>
          {children.map(child => (
            <TreeNode key={child.id || child._id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// Safe recursive flatten
function flatten(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  return nodes.reduce((acc, n) => {
    acc.push(n);
    if (Array.isArray(n.children) && n.children.length > 0) {
      acc.push(...flatten(n.children));
    }
    return acc;
  }, []);
}

// Normalise API response → always returns an array
function normaliseTree(data) {
  if (!data)                     return [];
  if (Array.isArray(data))       return data;
  if (Array.isArray(data.tree))  return data.tree;       // { tree: [...] }
  if (Array.isArray(data.data))  return data.data;       // { data: [...] }
  if (Array.isArray(data.children)) return data.children; // single root node
  return [];
}

export default function UserHierarchyPage() {
  const [tree, setTree]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await userController.getHierarchy(null);
      if (res.success) {
        setTree(normaliseTree(res.data));
      } else {
        setError(res.message || 'Failed to load hierarchy');
        setTree([]);
      }
    } catch (e) {
      setError(e.message || 'Unexpected error');
      setTree([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const all    = flatten(tree);
  const byRole = ['admin','super_distributor','distributor','subdistributor','retailer','employee'].map(r => ({
    role: r, count: all.filter(u => u.role === r).length,
  }));

  const q        = search.toLowerCase();
  const filtered = q
    ? tree.filter(n =>
        (n.fullName   || `${n.firstName||''} ${n.lastName||''}`).toLowerCase().includes(q) ||
        (n.email      || '').toLowerCase().includes(q) ||
        (n.customId   || '').toLowerCase().includes(q)
      )
    : tree;

  return (
    <div style={C.page}>
      {/* Header */}
      <div style={C.header}>
        <div>
          <h1 style={{ ...C.title, borderLeft: '4px solid #7c3aed', paddingLeft: '12px' }}>User Hierarchy</h1>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 16px' }}>
            {all.length} total users in tree
          </p>
        </div>
        <button style={{ ...C.btn, ...C.btnSec }} onClick={load}>
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {/* Role summary strip */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {byRole.filter(r => r.count > 0).map(r => (
          <div key={r.role} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 14px', backgroundColor: '#fff',
            borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,.07)',
            borderLeft: `3px solid ${ROLE_COLORS[r.role] || '#64748b'}`,
          }}>
            <Users size={14} color={ROLE_COLORS[r.role] || '#64748b'} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: ROLE_COLORS[r.role] || '#64748b' }}>
              {ROLE_LABELS[r.role] || r.role}
            </span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{r.count}</span>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ ...C.filterBar, marginBottom: '16px' }}>
        <div style={C.searchWrap}>
          <span style={{ ...C.searchIco, fontSize: '16px' }}>⌕</span>
          <input
            style={C.searchIn}
            placeholder="Search by name, email, ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tree card */}
      <div style={C.card}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
            <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{ marginTop: '12px' }}>Building tree…</div>
          </div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚠️</div>
            <div style={{ fontWeight: 600 }}>{error}</div>
            <button style={{ ...C.btn, ...C.btnSec, marginTop: '16px' }} onClick={load}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌳</div>
            <div style={{ fontSize: '15px', fontWeight: 600 }}>
              {search ? 'No results found' : 'No hierarchy data'}
            </div>
            {search && (
              <button style={{ ...C.btn, ...C.btnSec, marginTop: '14px' }} onClick={() => setSearch('')}>
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div style={{ padding: '20px' }}>
            {filtered.map(node => (
              <TreeNode key={node.id || node._id} node={node} depth={0} />
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}